/**
 * The builder: a stick figure with a skeleton under it.
 *
 * A pose is a handful of joint angles; a film eases between poses rather than between
 * pictures, which is what lets one figure walk, kneel, hammer and lift a wall without a
 * drawing for each. Limbs are measured from straight down, positive toward the way the
 * figure faces, so a pose drawn facing right is the same pose facing left.
 *
 * Where a hand has to be somewhere — on a cart's handle, under a wall's top plate — the
 * arm is solved for it (`reach`), two bones and the law of cosines, and the pose's own
 * angles for that arm are ignored.
 */

import type { Ink, Pt } from "./ink.js";
import { ease, mix } from "./timing.js";

export interface Pose {
  /** The torso's tilt off upright, forward positive. */
  lean: number;
  /** The head's tilt on the neck. */
  nod: number;
  /** The near arm: the shoulder's angle off straight down, and the elbow's bend. */
  armA: number;
  elbowA: number;
  armB: number;
  elbowB: number;
  /** The near leg: the hip's angle off straight down, and the knee's (negative bends it). */
  legA: number;
  kneeA: number;
  legB: number;
  kneeB: number;
}

export type Eyes = "open" | "closed" | "happy" | "wide";
export type Mouth = "smile" | "grin" | "open" | "o" | "flat";

export interface Figure {
  x: number;
  /** Where the lower foot rests: the pelvis is placed to stand on it. */
  ground?: number;
  /** Or the pelvis's height directly, for a figure sitting, falling or riding. */
  y?: number;
  facing: 1 | -1;
  scale?: number;
  eyes?: Eyes;
  mouth?: Mouth;
  /** How far the face turns toward the way the body faces: 0 looks at the camera. */
  look?: number;
  /** Hands held somewhere in particular, in stage units. */
  reachA?: Pt;
  reachB?: Pt;
  /** Something in the near hand, drawn along the forearm's direction. */
  hold?: (ink: Ink, hand: Pt, angle: number) => void;
  holdB?: (ink: Ink, hand: Pt, angle: number) => void;
  thumbA?: boolean;
  /** How far the cap has been knocked askew. */
  capTilt?: number;
  /** The figure from the neck up only — buried, or behind the wheel. */
  headOnly?: boolean;
}

const THIGH = 36;
const SHIN = 36;
const TORSO = 50;
const NECK = 7;
const HEAD = 15;
const UPPER = 29;
const FORE = 28;
const FOOT = 9;
const LIMB = 4.2;

export const STAND: Pose = {
  lean: 0.03,
  nod: 0,
  armA: 0.16,
  elbowA: 0.18,
  armB: -0.14,
  elbowB: 0.12,
  legA: 0.08,
  kneeA: -0.04,
  legB: -0.08,
  kneeB: -0.04,
};

export function blend(a: Pose, b: Pose, k: number): Pose {
  const out = { ...a };
  for (const key of Object.keys(a) as (keyof Pose)[]) {
    out[key] = mix(a[key], b[key], k);
  }
  return out;
}

/** Standing, breathing. */
export function idle(t: number): Pose {
  const breath = Math.sin(t * 2.4);
  return { ...STAND, armA: STAND.armA + breath * 0.03, armB: STAND.armB - breath * 0.03, nod: breath * 0.03 };
}

/** One step of a walk, `phase` in steps: a whole number is the near foot planted. */
export function walk(phase: number, stride = 1): Pose {
  const a = phase * Math.PI;
  const s = Math.sin(a);
  const swingA = Math.max(0, Math.cos(a));
  const swingB = Math.max(0, -Math.cos(a));
  return {
    lean: 0.08,
    nod: 0.04 * Math.sin(a * 2),
    armA: -0.38 * s * stride + 0.05,
    elbowA: 0.35 + 0.25 * Math.max(0, -s),
    armB: 0.38 * s * stride + 0.05,
    elbowB: 0.35 + 0.25 * Math.max(0, s),
    legA: 0.42 * s * stride,
    kneeA: -0.1 - 0.8 * swingA ** 1.5 * stride,
    legB: -0.42 * s * stride,
    kneeB: -0.1 - 0.8 * swingB ** 1.5 * stride,
  };
}

export function run(phase: number): Pose {
  const a = phase * Math.PI;
  const s = Math.sin(a);
  return {
    lean: 0.3,
    nod: -0.1,
    armA: -0.75 * s,
    elbowA: 1.5,
    armB: 0.75 * s,
    elbowB: 1.5,
    legA: 0.7 * s + 0.15,
    kneeA: -0.3 - 1.1 * Math.max(0, Math.cos(a)),
    legB: -0.7 * s + 0.15,
    kneeB: -0.3 - 1.1 * Math.max(0, -Math.cos(a)),
  };
}

export const KNEEL: Pose = {
  lean: 0.3,
  nod: 0.25,
  armA: 0.9,
  elbowA: 0.3,
  armB: 1.05,
  elbowB: 0.5,
  legA: 1.45,
  kneeA: -1.5,
  legB: 0.2,
  kneeB: -1.72,
};

/**
 * Kneeling with a hammer in the near hand. `phase` counts blows: the hammer comes up
 * slowly and down fast, and lands on every whole number.
 */
export function hammer(phase: number): Pose {
  const p = phase - Math.floor(phase);
  let arm: number;
  if (p < 0.72) {
    arm = mix(0.95, 2.7, ease.inOut(p / 0.72));
  } else {
    arm = mix(2.7, 0.95, ease.in((p - 0.72) / 0.28));
  }
  return { ...KNEEL, armA: arm, elbowA: mix(0.25, 0.95, (arm - 0.95) / 1.75) };
}

/** Standing and hammering at chest height: siding, a door. */
export function hammerStanding(phase: number): Pose {
  const p = phase - Math.floor(phase);
  const arm = p < 0.72 ? mix(1.2, 2.6, ease.inOut(p / 0.72)) : mix(2.6, 1.2, ease.in((p - 0.72) / 0.28));
  return { ...STAND, lean: 0.1, armA: arm, elbowA: mix(0.5, 1.0, (arm - 1.2) / 1.4), armB: 1.25, elbowB: 0.4 };
}

/** Whether a hammer has just landed, 0–1, for the little star at the nail. */
export function impact(phase: number): number {
  const p = phase - Math.floor(phase);
  return p < 0.16 ? 1 - p / 0.16 : 0;
}

export const SIT: Pose = {
  lean: 0.12,
  nod: 0.1,
  armA: 1.1,
  elbowA: 0.5,
  armB: 1.0,
  elbowB: 0.6,
  legA: 1.5,
  kneeA: -1.5,
  legB: 1.35,
  kneeB: -1.45,
};

export const CHEER: Pose = {
  lean: -0.05,
  nod: -0.2,
  armA: 2.55,
  elbowA: 0.25,
  armB: 2.35,
  elbowB: 0.3,
  legA: 0.18,
  kneeA: -0.05,
  legB: -0.18,
  kneeB: -0.05,
};

export const THUMBS_UP: Pose = {
  lean: -0.04,
  nod: -0.08,
  armA: 1.25,
  elbowA: 1.25,
  armB: -0.2,
  elbowB: 0.6,
  legA: 0.12,
  kneeA: -0.04,
  legB: -0.14,
  kneeB: -0.04,
};

/** Leaning back on a wall, ankles crossed: the job is done. */
export const LEAN_BACK: Pose = {
  lean: -0.16,
  nod: 0.06,
  armA: 1.25,
  elbowA: 1.25,
  armB: -0.5,
  elbowB: 0.9,
  legA: 0.38,
  kneeA: -0.05,
  legB: 0.05,
  kneeB: -0.02,
};

/** Braced to lift: knees bent, back straight, arms down to the load. */
export const CROUCH: Pose = {
  lean: 0.45,
  nod: -0.1,
  armA: 0.6,
  elbowA: 0.1,
  armB: 0.5,
  elbowB: 0.1,
  legA: 1.1,
  kneeA: -1.6,
  legB: 0.6,
  kneeB: -1.5,
};

function dir(facing: number, angle: number): Pt {
  return { x: facing * Math.sin(angle), y: Math.cos(angle) };
}

function along(from: Pt, d: Pt, length: number): Pt {
  return { x: from.x + d.x * length, y: from.y + d.y * length };
}

/** Two bones from `root` to `target`, the elbow on the low side. */
export function reach(root: Pt, target: Pt, upper: number, lower: number): { elbow: Pt; hand: Pt } {
  const dx = target.x - root.x;
  const dy = target.y - root.y;
  const distance = Math.min(upper + lower - 0.01, Math.max(Math.abs(upper - lower) + 0.01, Math.hypot(dx, dy)));
  const toward = Math.atan2(dy, dx);
  const bend = Math.acos((upper * upper + distance * distance - lower * lower) / (2 * upper * distance));
  const one = { x: root.x + Math.cos(toward + bend) * upper, y: root.y + Math.sin(toward + bend) * upper };
  const two = { x: root.x + Math.cos(toward - bend) * upper, y: root.y + Math.sin(toward - bend) * upper };
  const elbow = one.y > two.y ? one : two;
  const hand = { x: root.x + Math.cos(toward) * distance, y: root.y + Math.sin(toward) * distance };
  return { elbow, hand };
}

/** How far below the pelvis a leg's ankle is. */
function drop(leg: number, knee: number, scale: number): number {
  return (THIGH * Math.cos(leg) + SHIN * Math.cos(leg + knee)) * scale;
}

/** Where the figure's pelvis is, standing on its ground. */
export function pelvisOf(pose: Pose, figure: Figure): Pt {
  const scale = figure.scale ?? 1;
  const y =
    figure.y ??
    (figure.ground ?? 0) - Math.max(drop(pose.legA, pose.kneeA, scale), drop(pose.legB, pose.kneeB, scale)) - 2 * scale;
  return { x: figure.x, y };
}

export interface Joints {
  pelvis: Pt;
  shoulder: Pt;
  head: Pt;
  handA: Pt;
  handB: Pt;
}

/** Where the joints of a pose fall, without drawing it: a film hangs props off these. */
export function joints(pose: Pose, figure: Figure): Joints {
  const s = figure.scale ?? 1;
  const f = figure.facing;
  const pelvis = pelvisOf(pose, figure);
  const up = { x: f * Math.sin(pose.lean), y: -Math.cos(pose.lean) };
  const shoulder = along(pelvis, up, (TORSO - 5) * s);
  const neck = along(pelvis, up, TORSO * s);
  const head = along(neck, { x: f * Math.sin(pose.lean + pose.nod), y: -Math.cos(pose.lean + pose.nod) }, (NECK + HEAD) * s);
  const arm = (angle: number, bend: number, target: Pt | undefined): Pt => {
    if (target !== undefined) {
      return reach(shoulder, target, UPPER * s, FORE * s).hand;
    }
    return along(along(shoulder, dir(f, angle), UPPER * s), dir(f, angle + bend), FORE * s);
  };
  return {
    pelvis,
    shoulder,
    head,
    handA: arm(pose.armA, pose.elbowA, figure.reachA),
    handB: arm(pose.armB, pose.elbowB, figure.reachB),
  };
}

export function drawFigure(ink: Ink, pose: Pose, figure: Figure): Joints {
  const s = figure.scale ?? 1;
  const f = figure.facing;
  const p = ink.palette;
  const width = LIMB * s;
  const all = joints(pose, figure);
  const { pelvis, shoulder, head } = all;

  const legPoints = (leg: number, knee: number): Pt[] => {
    const kneeAt = along(pelvis, dir(f, leg), THIGH * s);
    const ankle = along(kneeAt, dir(f, leg + knee), SHIN * s);
    const toe = { x: ankle.x + f * FOOT * s * Math.cos(leg + knee), y: ankle.y + FOOT * s * 0.15 };
    return [pelvis, kneeAt, ankle, toe];
  };
  const armPoints = (angle: number, bend: number, target: Pt | undefined): Pt[] => {
    if (target !== undefined) {
      const { elbow, hand } = reach(shoulder, target, UPPER * s, FORE * s);
      return [shoulder, elbow, hand];
    }
    const elbow = along(shoulder, dir(f, angle), UPPER * s);
    return [shoulder, elbow, along(elbow, dir(f, angle + bend), FORE * s)];
  };
  const holding = (points: Pt[], hold: Figure["hold"]): void => {
    if (hold === undefined) {
      return;
    }
    const hand = points[2]!;
    const elbow = points[1]!;
    hold(ink, hand, Math.atan2(hand.y - elbow.y, hand.x - elbow.x));
  };

  if (figure.headOnly !== true) {
    // The far side first and a shade lighter, so the figure has a near and a far.
    const legB = legPoints(pose.legB, pose.kneeB);
    const armB = armPoints(pose.armB, pose.elbowB, figure.reachB);
    ink.poly(legB, false, { width, alpha: 0.55 });
    holding(armB, figure.holdB);
    ink.poly(armB, false, { width, alpha: 0.55 });
    ink.dot(armB[2]!.x, armB[2]!.y, 3 * s, p.ink, 0.55);

    const neck = along(pelvis, { x: f * Math.sin(pose.lean), y: -Math.cos(pose.lean) }, TORSO * s);
    ink.line(pelvis.x, pelvis.y, neck.x, neck.y, { width: width * 1.1, overshoot: false });
    ink.poly(legPoints(pose.legA, pose.kneeA), false, { width });
  }

  drawHead(ink, head, pose.lean + pose.nod, figure);

  if (figure.headOnly !== true) {
    const armA = armPoints(pose.armA, pose.elbowA, figure.reachA);
    holding(armA, figure.hold);
    ink.poly(armA, false, { width });
    const hand = armA[2]!;
    ink.dot(hand.x, hand.y, 3.2 * s);
    if (figure.thumbA === true) {
      ink.line(hand.x, hand.y, hand.x + f * 1.5 * s, hand.y - 9 * s, { width: width * 0.9, overshoot: false });
    }
  }
  return all;
}

function drawHead(ink: Ink, at: Pt, tilt: number, figure: Figure): void {
  const s = figure.scale ?? 1;
  const p = ink.palette;
  const { ctx } = ink;
  const look = figure.look ?? 0.6;
  ctx.save();
  ctx.translate(at.x, at.y);
  ctx.scale(figure.facing * s, s);
  ctx.rotate(tilt * 0.6);
  // Local units: the head is a circle of HEAD at the origin, facing +x.
  ink.circle(0, 0, HEAD, { fill: p.paper, offset: 0, width: 3.4 });

  const cx = look * 6;
  const eyes = figure.eyes ?? "open";
  for (const ex of [cx - 4.6, cx + 4.6]) {
    if (eyes === "open") {
      ink.dot(ex, -2, 1.9);
    } else if (eyes === "wide") {
      ink.circle(ex, -2.5, 3.2, { fill: p.paper, offset: 0, width: 1.4 });
      ink.dot(ex + 0.6, -2.2, 1.5);
    } else if (eyes === "happy") {
      ink.arc(ex, -0.5, 2.8, Math.PI * 1.1, Math.PI * 1.9, { width: 1.8 });
    } else {
      ink.line(ex - 2.4, -2, ex + 2.4, -2, { width: 1.8, overshoot: false });
    }
  }
  const mouth = figure.mouth ?? "smile";
  const mx = cx + 1;
  if (mouth === "smile") {
    ink.arc(mx, 3, 4.6, Math.PI * 0.2, Math.PI * 0.8, { width: 1.9 });
  } else if (mouth === "grin") {
    ink.shape(
      [
        { x: mx - 6, y: 4 },
        { x: mx + 6, y: 4 },
        { x: mx + 3.5, y: 9 },
        { x: mx - 3.5, y: 9 },
      ],
      { fill: p.ink, width: 1.6, offset: 0, overshoot: false },
    );
  } else if (mouth === "open") {
    ink.ellipse(mx, 7, 3, 3.8, { fill: p.ink, width: 1.4, offset: 0 });
  } else if (mouth === "o") {
    ink.circle(mx, 7, 2.6, { width: 1.6 });
  } else {
    ink.line(mx - 3.5, 7, mx + 3.5, 7, { width: 1.9, overshoot: false });
  }

  // The cap: an amber crown and a brim to the front.
  ctx.rotate(figure.capTilt ?? 0);
  const crown: Pt[] = [];
  for (let i = 0; i <= 10; i += 1) {
    const a = Math.PI + (i / 10) * Math.PI;
    crown.push({ x: Math.cos(a) * (HEAD + 1.5) - 0.5, y: -4 + Math.sin(a) * (HEAD * 0.82) });
  }
  ink.shape(crown, { fill: p.accent, width: 2.6, offset: 1, overshoot: false });
  const brim = 7 + 9 * Math.abs(look);
  ink.line(HEAD * 0.2, -4, HEAD * 0.2 + brim + 4, -2.5, { width: 4, color: p.ink, overshoot: false });
  ink.dot(-0.5, -4 - HEAD * 0.82, 2);
  ctx.restore();
}
